import CreateDuctSchedule from "../components/ductSchedule/CreateDuctSchedule";
import DuctStepModal from "../components/ductSchedule/DuctStepModal";
import DuctSystemModal from "../components/ductSchedule/DuctSystemModal";
import DuctSpecification from "../components/ductSchedule/DuctSpecification";
import DuctTemplateCopy from "../components/ductSchedule/DuctTemplateCopy";
import { ductSystemSteps } from "../components/ductSchedule/stepConfigurations";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { apiClient } from "../services/api/apiClient";
import { CircleStackIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import DuctSystemTable from "../components/ductSchedule/DuctSystemTable";

export default function DuctSchedule() {
  const selectedProjectId = useSelector(
    (state) => state.project.selectedProjectId
  );
  const [ductSubmittal, setDuctSubmittal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("Duct Schedule");
  const [isSystemModalOpen, setIsSystemModalOpen] = useState(false);
  const [isAddSystemModalOpen, setIsAddSystemModalOpen] = useState(false);
  const [isEditSystemModalOpen, setIsEditSystemModalOpen] = useState(false);
  const [editingSystem, setEditingSystem] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [shouldOpenSystemModal, setShouldOpenSystemModal] = useState(false);
  const [templateSystemData, setTemplateSystemData] = useState(null);

  // Fetch duct submittal when project is selected
  useEffect(() => {
    const fetchDuctSubmittal = async () => {
      if (!selectedProjectId) {
        setDuctSubmittal(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.get(
          `/duct-schedule/${selectedProjectId}`
        );

        // Handle the standardized response format from ResponseHelper
        if (response.success) {
          setDuctSubmittal(response.data);
        } else {
          setError(response.message || "Failed to load duct submittal");
          setDuctSubmittal(null);
        }
      } catch (err) {
        console.error("Error fetching duct submittal:", err);
        setError(`Failed to load duct submittal: ${err.message}`);
        setDuctSubmittal(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDuctSubmittal();
  }, [selectedProjectId]);

  const handleSubmittalCreated = (newSubmittal) => {
    setDuctSubmittal(newSubmittal);
    // Automatically open template modal after submittal creation
    setIsTemplateModalOpen(true);
  };

  // Effect to open system modal after submittal creation when blank template was selected
  useEffect(() => {
    if (ductSubmittal && shouldOpenSystemModal) {
      setIsAddSystemModalOpen(true);
      setShouldOpenSystemModal(false);
    }
  }, [ductSubmittal, shouldOpenSystemModal]);

  const handleAddSystem = async (systemData) => {
    try {
      const response = await apiClient.post(
        `/duct-system/${ductSubmittal.id}`,
        {
          data: systemData,
        }
      );

      if (response.success) {
        toast.success("System added successfully!");
        setIsAddSystemModalOpen(false); // Close the modal
        setTemplateSystemData(null); // Clear template data
        setRefreshTrigger((prev) => prev + 1); // Trigger refresh in DuctSystemTable
      } else {
        toast.error(response.message || "Failed to add system");
      }
    } catch (err) {
      console.error("Error adding system:", err);
      toast.error(err.message || "Failed to add system");
    }
  };

  const handleSaveAndContinue = async (systemData) => {
    try {
      const response = await apiClient.post(
        `/duct-system/${ductSubmittal.id}`,
        {
          data: systemData,
        }
      );

      if (response.success) {
        toast.success("System added successfully!");
        setRefreshTrigger((prev) => prev + 1); // Trigger refresh in DuctSystemTable

        // Close the system modal
        setIsAddSystemModalOpen(false);
        setTemplateSystemData(null);

        // Open the template modal for the next system
        setIsTemplateModalOpen(true);
      } else {
        toast.error(response.message || "Failed to add system");
      }
    } catch (err) {
      console.error("Error adding system:", err);
      toast.error(err.message || "Failed to add system");
    }
  };

  const handleDeleteSystem = async (systemId, systemTitle) => {
    if (window.confirm(`Are you sure you want to delete "${systemTitle}"?`)) {
      try {
        const response = await apiClient.delete(`/duct-system/${systemId}`);

        if (response.success) {
          toast.success("System deleted successfully!");
          setRefreshTrigger((prev) => prev + 1); // Trigger refresh in DuctSystemTable
        } else {
          toast.error(response.message || "Failed to delete system");
        }
      } catch (err) {
        console.error("Error deleting system:", err);
        toast.error(err.message || "Failed to delete system");
      }
    }
  };

  const handleEditSystem = (system) => {
    setEditingSystem(system);
    setIsEditSystemModalOpen(true);
  };

  const renderActiveTab = () => {
    if (!ductSubmittal || !ductSubmittal.id) {
      return <p>No duct submittal selected or found to display data.</p>;
    }

    switch (activeTab) {
      case "Duct Schedule":
        return (
          <DuctSystemTable
            ductSubmittalId={ductSubmittal.id}
            refreshTrigger={refreshTrigger}
            onDelete={handleDeleteSystem}
            onEdit={handleEditSystem}
          />
        );
      case "Specifications":
        return <DuctSpecification ductSubmittal={ductSubmittal} />;
      default:
        return null;
    }
  };

  if (!selectedProjectId) {
    return (
      <div className="flex flex-1 items-center justify-center min-h-screen bg-gray-100 p-4 overflow-y-auto">
        <p>Please select a project to view duct schedules.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center min-h-screen bg-gray-100 p-4 overflow-y-auto">
        <p>Loading duct schedule...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center min-h-screen bg-gray-100 p-4 overflow-y-auto">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  // Show create duct schedule interface if no submittal exists
  if (!ductSubmittal && selectedProjectId) {
    return (
      <div className="flex flex-1 items-center justify-center min-h-screen bg-gray-100 p-4 overflow-y-auto">
        <CreateDuctSchedule
          projectId={selectedProjectId}
          onSubmittalCreated={handleSubmittalCreated}
          onOpenModal={() => setIsSystemModalOpen(true)}
          onOpenSystemModal={() => setShouldOpenSystemModal(true)}
        />

        {/* System Modal */}
        <DuctStepModal
          isOpen={isSystemModalOpen}
          onClose={() => setIsSystemModalOpen(false)}
          steps={ductSystemSteps}
        />
      </div>
    );
  }

  // Main UI with tabs when submittal exists
  if (ductSubmittal && ductSubmittal.id) {
    return (
      <div className="p-4 md:p-8 bg-gray-100 min-h-screen flex-1 overflow-y-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div className="flex justify-center space-x-2 md:space-x-4 mb-4 md:mb-0 overflow-x-auto">
            <button
              onClick={() => setActiveTab("Duct Schedule")}
              className={`px-3 py-2 md:px-4 rounded-t-lg font-semibold text-sm md:text-base ${
                activeTab === "Duct Schedule"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-600"
              }`}
            >
              Duct Schedule
            </button>
            <button
              onClick={() => setActiveTab("Specifications")}
              className={`px-3 py-2 md:px-4 rounded-t-lg font-semibold text-sm md:text-base ${
                activeTab === "Specifications"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-600"
              }`}
            >
              Specifications
            </button>
          </div>

          {/* Action buttons similar to PipingSchedule */}
          <div className="flex space-x-2 md:space-x-4">
            <button
              onClick={() => setIsTemplateModalOpen(true)}
              className="bg-white text-gray-800 py-2 px-3 md:px-4 rounded-lg font-bold border border-gray-300 shadow-md text-sm md:text-base"
            >
              Add System
            </button>
            <button
              onClick={() => setIsSystemModalOpen(true)}
              className="bg-green-600 text-white py-2 px-3 md:px-4 rounded-lg font-bold border border-gray-300 shadow-md text-sm md:text-base"
            >
              Edit Specs
            </button>
            <button
              onClick={async () => {
                setGeneratingPDF(true);
                const toastId = toast.loading("Generating PDF...");

                try {
                  const response = await apiClient.get(
                    `/duct-pdf/${selectedProjectId}`
                  );

                  // Download the PDF if URL is available
                  if (response.success && response.data?.url) {
                    const link = document.createElement("a");
                    link.href = response.data.url;
                    link.download =
                      response.data.fileName || "duct-submittal.pdf";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    toast.success("PDF generated and download started!", {
                      id: toastId,
                    });
                  } else {
                    toast.error("PDF generated but no download URL received", {
                      id: toastId,
                    });
                  }
                } catch (err) {
                  console.error("Error generating PDF:", err);
                  toast.error(err.message || "Failed to generate PDF", {
                    id: toastId,
                  });
                } finally {
                  setGeneratingPDF(false);
                }
              }}
              disabled={generatingPDF}
              className={`${
                generatingPDF
                  ? "bg-indigo-400 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700"
              } text-white py-2 px-3 md:px-4 rounded-lg font-bold border border-gray-300 shadow-md text-sm md:text-base transition-colors`}
            >
              {generatingPDF ? "Generating..." : "Create Submittal"}
            </button>
          </div>
        </div>

        {/* Render content based on active tab */}
        {renderActiveTab()}

        {/* Add System Modal */}
        <DuctSystemModal
          isOpen={isAddSystemModalOpen}
          onClose={() => {
            setIsAddSystemModalOpen(false);
            setTemplateSystemData(null);
          }}
          onSubmit={handleAddSystem}
          onSaveAndContinue={handleSaveAndContinue}
          editingSystem={templateSystemData}
        />

        {/* Edit System Modal */}
        <DuctSystemModal
          isOpen={isEditSystemModalOpen}
          onClose={() => {
            setIsEditSystemModalOpen(false);
            setEditingSystem(null);
          }}
          onSubmit={async (data) => {
            if (!editingSystem) return;

            try {
              const response = await apiClient.put(
                `/duct-system/${editingSystem.id}`,
                { data }
              );

              if (response.success) {
                toast.success("System updated successfully!");
                setRefreshTrigger((prev) => prev + 1);
              } else {
                toast.error(response.message || "Failed to update system");
              }
            } catch (err) {
              console.error("Error updating system:", err);
              toast.error(err.message || "Failed to update system");
            } finally {
              setIsEditSystemModalOpen(false);
              setEditingSystem(null);
            }
          }}
          editingSystem={editingSystem}
        />

        {/* Advanced System Modal (10 Steps) */}
        <DuctStepModal
          isOpen={isSystemModalOpen}
          onClose={() => setIsSystemModalOpen(false)}
          steps={ductSystemSteps}
        />

        {/* Template Copy Modal */}
        <DuctTemplateCopy
          isOpen={isTemplateModalOpen}
          onClose={() => setIsTemplateModalOpen(false)}
          onSubmit={(selectedSystemId) => {
            toast.success("Template copied successfully!");
            setRefreshTrigger((prev) => prev + 1); // Trigger refresh in DuctSystemTable
            setIsTemplateModalOpen(false);
          }}
          submittalId={ductSubmittal?.id}
          onOpenSystemModal={() => setIsAddSystemModalOpen(true)}
          onOpenSystemModalWithTemplate={(templateData) => {
            setTemplateSystemData(templateData);
            setIsAddSystemModalOpen(true);
          }}
        />
      </div>
    );
  }

  // Fallback for any other state
  return (
    <div className="flex flex-1 items-center justify-center min-h-screen bg-gray-100 p-4 overflow-y-auto">
      <p>Preparing duct schedule data...</p>
    </div>
  );
}
